import type { StudioBlock, StudioBlockType, StudioMetricDef, StudioMetricTone, StudioTypeDef } from './studio-dashboard.types';

export const STUDIO_METRICS: StudioMetricDef[] = [
  { id: 'clicks', label: 'Clicks/Views/Interactions', initial: 'C', tone: 'clicks' },
  { id: 'impressions', label: 'Impressions', initial: 'I', tone: 'impressions' },
  { id: 'reach', label: 'Reach', initial: 'R' },
  { id: 'results', label: 'Results', initial: 'R' },
  { id: 'conversions', label: 'Conversions', initial: 'V', tone: 'conversions' },
  { id: 'ctr', label: 'CTR', initial: '%' },
  { id: 'spend', label: 'Cost', initial: '$', tone: 'spend' },
  { id: 'cpa', label: 'CPA', initial: '$' },
  { id: 'cpm', label: 'CPM', initial: '$' },
  { id: 'video_completion_rate', label: 'Video Completion Rate', initial: 'V' },
  { id: 'audio_completion_rate', label: 'Audio Completion Rate', initial: 'A' },
  { id: 'geo', label: 'Top Zip Codes', initial: 'Z' },
  { id: 'domain', label: 'Top Domains', initial: 'D' },
  { id: 'app', label: 'Top Apps', initial: 'A' },
  { id: 'dma', label: 'Top DMAs', initial: 'M' },
  { id: 'hourly', label: 'Hourly Impressions', initial: 'H' },
  { id: 'awp_completes', label: 'AWP Completes', initial: 'W' },
];

export const STUDIO_BLOCK_TYPES: StudioTypeDef[] = [
  {
    type: 'chart',
    label: 'Performance chart',
    caption: 'Trend line renders live campaign data once this dashboard goes live.',
  },
  {
    type: 'table',
    label: 'Campaign table',
    caption: 'Campaign / Line Item / Creative breakdown, updated live once this dashboard goes live.',
  },
  {
    type: 'domain',
    label: 'Domain performance',
    columnLabel: 'Domain',
    emptyText: 'No domain data for this range.',
  },
  {
    type: 'app',
    label: 'App performance',
    columnLabel: 'App',
    emptyText: 'No app data for this range.',
  },
  {
    type: 'zip',
    label: 'Zip code performance',
    columnLabel: 'Zip code',
    emptyText: 'No zip code data for this range.',
  },
  {
    type: 'geo',
    label: 'Geographic distribution',
    isGeo: true,
    title: 'Geographic data unavailable',
    desc: 'Postal and regional heatmaps need location signals from your data source. Those signals are often withheld for privacy, small audiences, or certain inventory types.',
  },
];

export const METRIC_TONE_CLASSES: Record<StudioMetricTone, string> = {
  clicks: 'bg-[#F59E0B]',
  conversions: 'bg-[#2563EB]',
  impressions: 'bg-[#059669]',
  spend: 'bg-[#EF4444]',
};

export const GRID_SPAN_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
};

export function defaultSpanFor(type: StudioBlockType): 1 | 2 | 3 | 4 {
  if (type === 'metric') {
    return 1;
  }
  if (type === 'chart' || type === 'table') {
    return 4;
  }
  return 2;
}

export function clampStudioSpan(span: number): 1 | 2 | 3 | 4 {
  if (span <= 1) {
    return 1;
  }
  if (span >= 4) {
    return 4;
  }
  return span as 1 | 2 | 3 | 4;
}

export const STUDIO_DEFAULT_ACCOUNT_ID = 'default';

export function formatStudioPlatformName(platform: string | undefined): string {
  if (!platform || platform === 'Default') {
    return 'Default';
  }
  return platform
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

let studioBlockSeq = 0;

export function createStudioBlockId(): string {
  studioBlockSeq += 1;
  return `studio-block-${Date.now()}-${studioBlockSeq}`;
}

export function createDefaultStudioBlocks(): StudioBlock[] {
  return [
    { id: createStudioBlockId(), type: 'metric', metricId: 'clicks', span: 1 },
    { id: createStudioBlockId(), type: 'metric', metricId: 'conversions', span: 1 },
    { id: createStudioBlockId(), type: 'metric', metricId: 'impressions', span: 1 },
    { id: createStudioBlockId(), type: 'metric', metricId: 'spend', span: 1 },
    { id: createStudioBlockId(), type: 'chart', span: 4 },
    { id: createStudioBlockId(), type: 'table', span: 4 },
  ];
}
